import React, { useState, useRef, useEffect } from 'react'
import Globals from 'system/Globals'
import { animEventEmitter } from 'system/utils/eventEmitter'

const CHROME_HEIGHT = 34

function WEBVIEWComponent(props) {
    const refContent = useRef()
    const [posStyle, setPosStyle] = useState({})
    const [webviewUrl, setWebviewUrl] = useState(null)
    const [currentUrl, setCurrentUrl] = useState(null)
    const [iframeScale, setIframeScale] = useState(null)
    // Gate the BrowserView attach on the slide's animate-in lifecycle —
    // Page.runLayerAnim invokes our registered onAnim callback when the slide
    // becomes active, which works for default and controller modes alike
    // (including the first page where the standard intersection signal misses).
    const [isActive, setIsActive] = useState(false)
    const [moduleOpen, setModuleOpen] = useState(false)

    useEffect(() => {
        const fmt = Globals.instance().getCanvasFormat(props.model.formats, props.format)
        if (!fmt) {
            if (props.onLoad) props.onLoad(props.index)
            return
        }
        if (fmt.style) {
            setPosStyle({
                position: 'absolute',
                left: fmt.style.left,
                top: fmt.style.top,
                width: fmt.style.width,
                height: fmt.style.height,
                opacity: fmt.style.opacity
            })
        }
        const url = fmt.webview_url || null
        setWebviewUrl(url)
        setCurrentUrl(url)
        if (props.onLoad) props.onLoad(props.index)
    }, [props.model.formats, props.format]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handler = (e) => {
            if (e.detail && e.detail.event === 'webview-url-updated') {
                setCurrentUrl(e.detail.url)
            }
            if (e.detail && e.detail.event === 'narrative-menu-open-full') {
                if (Globals.instance().electron) window.api.send("toApp", { event: "webview-hide" })
            }
            if (e.detail && e.detail.event === 'module-open-state') {
                setModuleOpen(!!e.detail.hasModule)
            }
        }
        window.addEventListener('window-event', handler)
        return () => window.removeEventListener('window-event', handler)
    }, [])

    useEffect(() => {
        if (!Globals.instance().electron) return
        if (!props.pageId) return
        const handler = (newValue) => {
            setIsActive(newValue === props.pageId)
        }
        animEventEmitter.on('valueChanged', handler)
        return () => { animEventEmitter.off('valueChanged', handler) }
    }, [props.pageId])

    useEffect(() => {
        if (!webviewUrl || !posStyle.width) return
        // Hide the BrowserView when a module overlay (gallery / timeline / map / etc.)
        // is open — the BrowserView is OS-level on top of the React renderer and
        // would otherwise occlude the module. Re-shows on next effect run when
        // moduleOpen flips back to false (and the slide is still active).
        if (Globals.instance().electron && (!isActive || moduleOpen)) return

        const canvasW = props.format && props.format.canvas_width
            ? parseFloat(props.format.canvas_width) : 1920
        const canvasH = props.format && props.format.canvas_height
            ? parseFloat(props.format.canvas_height) : 1080

        const wW = window.innerWidth
        const wH = window.innerHeight

        const leftPct  = parseFloat(posStyle.left   || '0')
        const topPct   = parseFloat(posStyle.top    || '0')
        const widthPct = parseFloat(posStyle.width  || '100')
        const heightPct= parseFloat(posStyle.height || '100')

        // Canvas scale: ratio of window pixels to authored canvas pixels
        const zoom = Math.round((wW / canvasW) * 1000) / 1000

        if (Globals.instance().electron) {
            const normalizedUrl = webviewUrl.startsWith('http://') || webviewUrl.startsWith('https://')
                ? webviewUrl : 'https://' + webviewUrl

            // Compute bounds analytically — avoids getBoundingClientRect timing
            // issues where the flex layout may not be resolved when the effect fires.
            // Chrome bar occupies the top CHROME_HEIGHT px; BrowserView fills below it.
            window.api.send("toApp", {
                event: "webview-show",
                url: normalizedUrl,
                zoom,
                bounds: {
                    x: Math.round(leftPct  / 100 * wW),
                    y: Math.round(topPct   / 100 * wH) + CHROME_HEIGHT,
                    width:  Math.round(widthPct  / 100 * wW),
                    height: Math.round(heightPct / 100 * wH) - CHROME_HEIGHT
                }
            })

            return () => {
                window.api.send("toApp", { event: "webview-hide" })
            }
        }

        // Web iframe fallback: set iframe to authored pixel dimensions and scale down
        setIframeScale({
            zoom,
            w: Math.round(widthPct  / 100 * canvasW),
            h: Math.round(heightPct / 100 * canvasH)
        })
    }, [webviewUrl, posStyle, isActive, moduleOpen]) // eslint-disable-line react-hooks/exhaustive-deps

    const sendNav = (event) => {
        if (Globals.instance().electron) window.api.send("toApp", { event })
    }

    const chromeBar = (
        <div style={{
            height: CHROME_HEIGHT + 'px',
            minHeight: CHROME_HEIGHT + 'px',
            background: '#c8c8c8',
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px',
            gap: '5px',
            flexShrink: 0,
            userSelect: 'none'
        }}>
            <svg onClick={() => sendNav('webview-back')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
            <svg onClick={() => sendNav('webview-forward')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
            </svg>
            <svg onClick={() => sendNav('webview-reload')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
            <div style={{flex: 1, background: '#c8c8c8', borderRadius: '4px', height: '18px', display: 'flex', alignItems: 'center', padding: '0 6px', overflow: 'hidden'}}>
                <span style={{fontSize: '9px', color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.4}}>
                    {currentUrl || webviewUrl || ''}
                </span>
            </div>
            <svg onClick={() => sendNav('webview-fullscreen')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
            </svg>
        </div>
    )

    if (Globals.instance().electron) {
        return (
            <div style={posStyle}>
                <div style={{width:'100%', height:'100%', filter:'drop-shadow(0 24px 48px rgba(0,0,0,0.5))', borderRadius:'8px'}}>
                    <div style={{width:'100%', height:'100%', background:'#aaa', display:'flex', flexDirection:'column', overflow:'hidden', borderRadius:'8px'}}>
                        {chromeBar}
                        <div style={{flex:1, background:'#fff'}} />
                    </div>
                </div>
            </div>
        )
    }

    const iframeStyle = iframeScale ? {
        position: 'absolute',
        top: 0,
        left: 0,
        width:  iframeScale.w + 'px',
        height: iframeScale.h + 'px',
        border: 'none',
        transform: `scale(${iframeScale.zoom})`,
        transformOrigin: 'top left'
    } : { width: '100%', height: '100%', border: 'none' }

    return (
        <div style={posStyle}>
            <div style={{width:'100%', height:'100%', filter:'drop-shadow(0 24px 48px rgba(0,0,0,0.5))', borderRadius:'8px'}}>
                <div style={{width:'100%', height:'100%', background:'#aaa', display:'flex', flexDirection:'column', overflow:'hidden', borderRadius:'8px'}}>
                    {chromeBar}
                    <div ref={refContent} style={{flex:1, background:'#fff', overflow:'hidden', position:'relative'}}>
                        {webviewUrl && (
                            <iframe
                                src={webviewUrl}
                                style={iframeStyle}
                                title="webview"
                                allow="fullscreen"
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export { WEBVIEWComponent as WEBVIEW }
